function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
// Lib Imports
import React, { memo, useMemo, useContext, useEffect } from 'react';
import { useTable, useRowSelect, useSortBy, useExpanded, useColumnOrder } from 'react-table';
import _ from 'lodash';
import { ThemeContext } from 'grommet';
import { deepMerge } from 'grommet/utils';
import PropTypes from 'prop-types';

// Core Imports
import { getGrandEmitter } from 'granite-admin/utils/grandEmitter';
import { DndContext, Container, Draggable } from 'granite-admin/core/components/DndSuite';

// Application Imports
import tableDefaultProps from '../defaultProps';
import propTypes from '../propTypes';
import TableContainer from './baseStyles';
import { classes, useDeepCompareEffect, useThrottleEffect, useDeepCompareMemo } from '../utils';
import { useFlexLayout, useResizeColumns, useSelectColumn, useUtilsColumns } from '../plugins';
import { Body, Row, Header, DeferMount } from '../components';
const getRowId = (row, relativeIndex) => row.uniqueId ? row.uniqueId : row?.pk ? row?.pk : relativeIndex;
const Table = /*#__PURE__*/memo(tableProps => {
  // const emptyProps = useMemo(() => ({}), []);
  const grandEmitter = useMemo(() => getGrandEmitter(), []);
  const {
    graniteTable: {
      defaultProps
    } = {}
  } = useContext(ThemeContext) ?? {};
  const mergedDefaultProps = useMemo(() => deepMerge(tableDefaultProps, defaultProps), []);
  const props = useDeepCompareMemo(() => ({
    ...mergedDefaultProps,
    ...tableProps
  }), [mergedDefaultProps, tableProps]);
  const {
    initialState
  } = props;
  const iState = useDeepCompareMemo(() => initialState, [initialState]);
  const {
    id,
    data,
    columns,
    className,
    style,
    showPagination,
    showPaginationTop,
    showPaginationBottom,
    useControlledState,
    defaultColumn,
    selectable,
    manualSortBy,
    selectColumnPosition,
    TableComponent,
    TheadComponent,
    TbodyComponent,
    NoDataComponent,
    LoadingComponent,
    PaginationComponent,
    SortIconComponent,
    TrComponent,
    TdComponent,
    ThComponent,
    noDataText,
    loading,
    loadingText,
    expandable,
    draggable,
    reorderable,
    RowSubComponent,
    BulkActionsComponent,
    label,
    bulkActions,
    pageData,
    showSizeChanger,
    resizable,
    deleteBulk,
    pageSizeOptions,
    noHeader,
    showStrippedRows,
    expandAllSubRows,
    getGhostParent,
    dragClass,
    dropClass,
    eventListener = {},
    ...rest
  } = props;
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state,
    visibleColumns,
    // allColumns,
    toggleAllRowsSelected,
    selectedFlatRows,
    // setHiddenColumns,
    setColumnOrder,
    toggleAllRowsExpanded
  } = useTable({
    data,
    columns,
    useControlledState,
    initialState: iState,
    defaultColumn,
    manualSortBy,
    expandable,
    draggable,
    selectable,
    reorderable,
    autoResetSortBy: false,
    autoResetExpanded: false,
    autoResetHiddenColumns: false,
    grandEmitter,
    id,
    getRowId,
    ...rest
  }, useFlexLayout, reorderable && useColumnOrder, useSortBy, resizable && useResizeColumns, expandable && useExpanded, useRowSelect,
  // useActionsColumn,
  selectable && useSelectColumn, useUtilsColumns);
  const {
    columnResizing,
    sortBy,
    expanded,
    hiddenColumns,
    columnOrder
  } = state;
  useThrottleEffect(() => {
    let initialState = _.pickBy({
      ...iState,
      columnResizing: iState.columnResizing?.columnWidths
    }, _.identity);
    let currentState = _.pickBy({
      sortBy,
      columnResizing: columnResizing?.columnWidths,
      expanded,
      hiddenColumns,
      columnOrder
    }, _.identity);
    if (columnResizing?.isResizingColumn || _.isEqual(initialState, currentState)) return;
    grandEmitter.emit(`${id}__TABLE_STATE_EVENT`, {
      sortBy,
      columnResizing,
      expanded,
      hiddenColumns,
      columnOrder
    });
  }, [sortBy, columnResizing, hiddenColumns, columnOrder, iState, expanded], 1000);
  useDeepCompareEffect(() => {
    grandEmitter.emit(`${id}__SELECTION_EVENT`, selectedFlatRows?.map(i => i.original));
  }, [grandEmitter, id, selectedFlatRows]);
  const closeBulkActions = () => {
    toggleAllRowsSelected(false);
  };
  const reorderingData = (containerId, {
    removedIndex: startIndex,
    addedIndex: endIndex
  }) => {
    const newData = [...data];
    const [movedRow] = newData.splice(startIndex, 1);
    newData.splice(endIndex, 0, movedRow);
    // setData({ data: newData, count: newData.length });
    // console.log('newData', newData);
    grandEmitter.emit(`${id}__REORDER_EVENT`, newData);
  };
  const handleDragEnd = (groupName, result) => {
    const {
      removedIndex,
      addedIndex
    } = result;
    if (removedIndex === addedIndex) return;
    reorderingData(groupName, result);
  };
  const onPageChange = data => {
    grandEmitter.emit(`${id}__PAGINATION_EVENT`, data);
  };
  const onRefresh = () => {
    grandEmitter.emit(`${id}__REFRESH_EVENT`);
  };
  const {
    globalOnDragEnd,
    globalShouldAcceptDrop,
    contextId
  } = useContext(DndContext) ?? {};
  useEffect(() => {
    if (expandAllSubRows && toggleAllRowsExpanded) toggleAllRowsExpanded(true);
  }, [expandAllSubRows, toggleAllRowsExpanded]);
  const modifiedEventListener = row => {
    const modifiedFunc = {};
    const funcList = Object.keys(eventListener);
    funcList.forEach(func => {
      modifiedFunc[func] = () => eventListener[func](row);
    });
    return modifiedFunc;
  };
  return /*#__PURE__*/React.createElement(TableContainer, {
    className: className,
    style: style,
    id: id,
    showStrippedRows: showStrippedRows
  }, showPagination && showPaginationTop ? /*#__PURE__*/React.createElement("div", {
    className: "pagination-top"
  }, /*#__PURE__*/React.createElement(PaginationComponent, null)) : null, /*#__PURE__*/React.createElement(TableComponent, _extends({}, getTableProps(), {
    className: classes()
  }), !noHeader && /*#__PURE__*/React.createElement(Header, {
    TheadComponent: TheadComponent,
    ThComponent: ThComponent,
    TrComponent: TrComponent,
    headerGroups: headerGroups,
    getTableBodyProps: getTableBodyProps,
    setColumnOrder: setColumnOrder,
    flatColumns: visibleColumns,
    reorderable: reorderable,
    resizable: resizable,
    SortIconComponent: SortIconComponent
  }), /*#__PURE__*/React.createElement(Body, _extends({
    TbodyComponent: TbodyComponent
  }, getTableBodyProps(), {
    // ref={provided.innerRef}
    // {...provided.droppableProps}
    className: classes('')
  }), /*#__PURE__*/React.createElement(DragDropWrapper, {
    onDragEnd: handleDragEnd,
    groupName: `${id}__droppable`,
    globalOnDragEnd: globalOnDragEnd,
    globalShouldAcceptDrop: globalShouldAcceptDrop,
    draggable: draggable,
    data: data,
    contextId: contextId,
    getGhostParent: getGhostParent,
    dragClass: dragClass,
    dropClass: dropClass
  }, rows.length ? /*#__PURE__*/React.createElement(DeferMount, {
    chunkSize: 5
  }, rows.map(row => {
    prepareRow(row);
    return /*#__PURE__*/React.createElement(Draggable, {
      key: `${String(row.original?.uniqueId ?? row.original?.pk)}` ?? row.original?._type ?? row.id,
      index: row.index
    }, /*#__PURE__*/React.createElement(Row, _extends({
      TrComponent: TrComponent,
      TdComponent: TdComponent,
      row: row,
      cells: row.cells
    }, row.getRowProps(), {
      onClick: props.rowClickHandler
    }, modifiedEventListener(row))), row.isExpanded ? /*#__PURE__*/React.createElement(TrComponent, {
      className: "rt-tr-subrow"
    }, /*#__PURE__*/React.createElement(RowSubComponent, {
      row: row,
      tableProps: props,
      tableState: state
    })) : null);
  })) : /*#__PURE__*/React.createElement(TrComponent, null, /*#__PURE__*/React.createElement(NoDataComponent, null, noDataText))))), showPagination && showPaginationBottom ? /*#__PURE__*/React.createElement("div", {
    className: "pagination-bottom"
  }, /*#__PURE__*/React.createElement(PaginationComponent, {
    pageData: pageData,
    showSizeChanger: showSizeChanger,
    onPageChange: onPageChange,
    onRefresh: onRefresh,
    pageSizeOptions: pageSizeOptions
  })) : null, /*#__PURE__*/React.createElement(LoadingComponent, {
    loading: loading,
    loadingText: loadingText
  }), !!bulkActions?.length && !!selectedFlatRows.length && /*#__PURE__*/React.createElement(BulkActionsComponent, {
    selected: selectedFlatRows.map(row => row.original),
    close: closeBulkActions,
    label: label,
    bulkActions: bulkActions
  }));
});
Table.displayName = 'Table';

// Table.defaultProps = tableDefaultProps;

Table.propTypes = propTypes;
const DragDropWrapper = ({
  children,
  onDragEnd,
  draggable,
  globalOnDragEnd,
  globalShouldAcceptDrop,
  groupName,
  data,
  contextId,
  dragClass = 'dragging',
  dropClass = 'dropping',
  ...rest
}) => {
  if (!draggable) return children;
  return /*#__PURE__*/React.createElement(Container, _extends({
    groupName: groupName,
    getChildPayload: index => ({
      sourceContainerId: groupName,
      draggedItem: data[index],
      contextId
    }),
    onDrop: data => {
      if (data.removedIndex === null && data.addedIndex === null) return;
      if (globalOnDragEnd) globalOnDragEnd(groupName, data);else onDragEnd(groupName, data);
    },
    shouldAcceptDrop: (sourceContainerOptions, payload) => globalShouldAcceptDrop ? globalShouldAcceptDrop(groupName, sourceContainerOptions, payload) : true,
    dragClass: dragClass,
    dropClass: dropClass
  }, rest), children);
};
DragDropWrapper.propTypes = {
  children: PropTypes.node,
  onDragEnd: PropTypes.func,
  draggable: PropTypes.bool,
  globalOnDragEnd: PropTypes.func,
  globalShouldAcceptDrop: PropTypes.func,
  groupName: PropTypes.string,
  data: PropTypes.array,
  getGhostParent: PropTypes.any
};
export default Table;